import currencies from '@/assets/currencies';

export const DISPLAY_MAX_DECIMALS = 3;

const DEFAULT_LOCALE = 'en-GB';

/** Keys must be uppercase ISO 4217 codes — normalisation happens in formatCurrencyAmount. */
const CURRENCY_LOCALE_MAP = {
  GBP: 'en-GB',
  EUR: 'de-DE',
  USD: 'en-US',
  UAH: 'uk-UA',
};

const roundForDisplay = (num, maxDecimals) => {
  const rounded = Number(num.toFixed(maxDecimals));
  return Object.is(rounded, -0) ? 0 : rounded;
};

/** Precondition: currencyCode is a valid uppercase ISO 4217 string or undefined. */
const formatWithIntl = (rounded, locale, maxDecimals, currencyCode) => {
  const options = {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
    ...(currencyCode
      ? { style: 'currency', currency: currencyCode, currencyDisplay: 'symbol' }
      : {}),
  };

  return new Intl.NumberFormat(locale, options).format(rounded);
};

const formatValue = (value, { maxDecimals, locale, currencyCode }) => {
  if (value == null || value === '') {
    return '';
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return String(value);
  }

  return formatWithIntl(roundForDisplay(num, maxDecimals), locale, maxDecimals, currencyCode);
};

export const formatDisplayNumber = (value, { maxDecimals = DISPLAY_MAX_DECIMALS } = {}) =>
  formatValue(value, {
    maxDecimals,
    locale: DEFAULT_LOCALE,
  });

/**
 * currencyCode is normalised to uppercase before being forwarded.
 * prefixSymbol: when true, places the symbol (from currencies.json) before the
 * formatted number with en-GB separators — use for tables and lists to keep
 * all currencies consistent regardless of locale conventions.
 */
export const formatCurrencyAmount = (
  amount,
  currencyCode,
  { maxDecimals = DISPLAY_MAX_DECIMALS, prefixSymbol = false } = {},
) => {
  if (!currencyCode) {
    return '';
  }

  const normalizedCurrencyCode = String(currencyCode).toUpperCase();

  if (prefixSymbol) {
    const numberStr = formatDisplayNumber(amount, { maxDecimals });
    if (numberStr === '') return '';
    const entry = currencies[0] && currencies[0][normalizedCurrencyCode];
    const symbol = (entry && entry.symbol) || normalizedCurrencyCode;
    return `${symbol}${numberStr}`;
  }

  return formatValue(amount, {
    maxDecimals,
    locale: CURRENCY_LOCALE_MAP[normalizedCurrencyCode] || DEFAULT_LOCALE,
    currencyCode: normalizedCurrencyCode,
  });
};
